import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Star, Clock, Users, Award, Heart, Leaf, Baby } from 'lucide-react';
import { BOOKING_URL } from "@/constants/urls";
import fertilityImageWebP from "@/assets/family.webp";
import fertilityImageJPG from "@/assets/family.jpg";

const FertilityPackagePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 md:px-6 bg-sage-50 border-b border-amber-100">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 mb-4">
                🌿 Limited Time Fertility Special 🌿
              </Badge>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent leading-tight mb-4">
                Chinese Medicine + Women&apos;s Health Nutrition
              </h1>
              <p className="text-lg md:text-xl text-sage-700 leading-relaxed mb-2">
                Two experienced practitioners. One supportive journey.
              </p>
              <p className="text-md text-sage-600 leading-relaxed mb-4">
                Preparing for pregnancy is more than just timing—it's about nourishing your whole self, understanding your cycle, and feeling supported every step of the way.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4 mb-8">
                <Button
                  asChild
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
                >
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Your Fertility Journey
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-4 text-lg font-medium"
                >
                  <a href="#package-details">View Package Details</a>
                </Button>
              </div>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <div className="w-full max-w-md">
                <picture>
                  <source srcSet={fertilityImageWebP} type="image/webp" />
                  <img
                    src={fertilityImageJPG}
                    alt="Family planning and fertility support through Chinese medicine and women's health dietitian care"
                    className="rounded-2xl shadow-2xl w-full h-auto"
                    loading="eager"
                    decoding="async"
                    aria-describedby="fertility-package-image-description"
                  />
                </picture>
              </div>
              <div id="fertility-package-image-description" className="sr-only">
                Professional fertility support combining Chinese medicine acupuncture with women&apos;s health dietitian nutrition for comprehensive preconception care and family planning.
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white rounded-lg shadow-lg p-4 border border-amber-200">
                <div className="flex items-center space-x-2">
                  <Baby className="h-5 w-5 text-amber-500 fill-current" />
                  <span className="text-sm font-medium text-sage-700">Limited Time Offer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 px-4 md:px-6 bg-white border-b border-amber-100">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              A Full-Circle Approach to Fertility
            </h2>
            <p className="text-lg text-sage-700 leading-relaxed mb-6">
              At <em>Women and the Way</em> on the Sunshine Coast, we've created a limited-time fertility package that combines the wisdom of Chinese medicine with dietitian-led women&apos;s health nutrition—helping you feel empowered, prepared, and deeply held.
            </p>
            <p className="text-lg text-sage-700 leading-relaxed">
              This is not a generic plan—it's a <strong>two-practitioner approach</strong> designed for women who want real answers, holistic support, and a clear path forward.
            </p>
          </div>
        </div>
      </section>

      {/* Package Details Section */}
      <section id="package-details" className="py-16 px-4 md:px-6 bg-sage-50 border-b border-amber-100">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              🧡 What's Included
            </h2>
          </div>

          <Card className="border-2 border-amber-200 shadow-xl bg-white max-w-5xl mx-auto mb-12">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl font-serif text-sage-800">
                Fertility Special Package
              </CardTitle>
              <CardDescription className="text-lg text-sage-600">
                Chinese Medicine + Women&apos;s Health Nutrition Support
              </CardDescription>
              <div className="flex items-center justify-center space-x-4 mt-4">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-amber-500" />
                  <span className="text-sm text-sage-600">Two practitioners</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Award className="h-4 w-4 text-amber-500" />
                  <span className="text-sm text-sage-600">Comprehensive approach</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Chinese Medicine Section */}
              <div className="bg-sage-50 rounded-lg p-6">
                <h3 className="font-semibold text-sage-800 text-xl mb-4 flex items-center">
                  <Leaf className="h-5 w-5 text-amber-500 mr-2" />
                  1. Initial Fertility Acupuncture Consult
                </h3>
                <p className="text-sage-700 mb-3">
                  <strong>with Dr. Kirby Quin, Chinese Medicine Practitioner & Acupuncturist</strong>
                </p>
                <p className="text-sage-600">
                  Supporting hormonal balance, cycle regulation, fertility awareness, and emotional wellbeing through gentle, evidence-informed acupuncture and herbal guidance.
                </p>
              </div>

              {/* Women's health dietitian — nutrition consult */}
              <div className="bg-amber-50 rounded-lg p-6">
                <h3 className="font-semibold text-sage-800 text-xl mb-4 flex items-center">
                  <Heart className="h-5 w-5 text-amber-500 mr-2" />
                  2. Initial Fertility Nutrition Consult
                </h3>
                <p className="text-sage-700 mb-3">
                  <strong>with Rachel Hazlett, Women&apos;s Health Dietitian</strong>
                </p>
                <p className="text-sage-600">
                  Personalised nutrition for hormone balance, digestion, nutrient needs, and energy—tailored whether you are trying to conceive, already pregnant, or managing conditions such as PCOS or endometriosis.
                </p>
              </div>

              {/* Bonus eBooks Section */}
              <div className="bg-white border border-amber-200 rounded-lg p-6">
                <h3 className="font-semibold text-sage-800 text-xl mb-4 flex items-center">
                  <Star className="h-5 w-5 text-amber-500 mr-2" />
                  3. Bonus eBooks from Both Practitioners
                </h3>
                <p className="text-sage-600 mb-3">
                  Dive deeper with take-home tools, charts, and insights to support your fertility journey at home.
                </p>
                <ul className="text-sage-600 space-y-1">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Cycle tracking tips</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Nutritional guidance</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>Herbal support recommendations</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>And more fertility insights</span>
                  </li>
                </ul>
              </div>

              {/* Pricing Section */}
              <div className="bg-gradient-to-r from-amber-50 to-sage-50 rounded-lg p-6 text-center">
                <h3 className="font-semibold text-sage-800 text-xl mb-4">💰 Special Package Pricing</h3>
                <div className="space-y-2">
                  <p className="text-lg text-sage-700">
                    <span className="line-through text-sage-500">Valued at $370</span>
                  </p>
                  <p className="text-2xl font-bold text-amber-600">
                    Special Price: $250
                  </p>
                  <p className="text-sage-600 font-medium">
                    (That's over 30% off!)
                  </p>
                  <p className="text-sm text-sage-600 mt-3">
                    Includes two separate one-on-one consultations + bonus eBooks
                  </p>
                </div>
              </div>

              <div className="text-center pt-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
                >
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book Your Fertility Package
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Meet Your Practitioners Section */}
      <section className="py-16 px-4 md:px-6 bg-white border-b border-amber-100">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              Meet Your Practitioners
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Dr. Kirby Quin */}
            <Card className="border-2 border-sage-200 shadow-lg bg-white">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Leaf className="h-8 w-8 text-sage-600" />
                </div>
                <CardTitle className="text-xl font-serif text-sage-800">
                  🌸 Dr. Kirby Quin
                </CardTitle>
                <CardDescription className="text-sage-600">
                  Chinese Medicine Practitioner & Acupuncturist
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sage-700 leading-relaxed">
                  With over 10 years of experience, Kirby supports women through every life stage—offering gentle acupuncture and herbal medicine for fertility, pregnancy, birth preparation, and postpartum recovery. Her clients love her down-to-earth, caring style and commitment to evidence-informed care.
                </p>
              </CardContent>
            </Card>

            {/* Rachel Hazlett */}
            <Card className="border-2 border-amber-200 shadow-lg bg-white">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-amber-600" />
                </div>
                <CardTitle className="text-xl font-serif text-sage-800">
                  🌼 Rachel Hazlett
                </CardTitle>
                <CardDescription className="text-sage-600">
                  Women&apos;s Health Dietitian
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sage-700 leading-relaxed">
                  Rachel is an experienced dietitian specialising in women&apos;s health, with more than ten years in practice. She supports women through fertility, pregnancy, and hormone-related concerns—including PCOS, endometriosis, and digestive issues—and those who want clearer energy and day-to-day eating habits. Consultations are thorough and practical: she reviews your history, relevant blood tests, supplements, gut health, and current diet to build a sustainable plan. Rachel sees clients in clinic and via telehealth.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why This Package Section */}
      <section className="py-16 px-4 md:px-6 bg-sage-50 border-b border-amber-100">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              💫 Why This Package?
            </h2>
          </div>

          <Card className="border-2 border-amber-200 shadow-lg bg-white">
            <CardContent className="p-8">
              <div className="space-y-4 text-center">
                <p className="text-lg text-sage-700 leading-relaxed">
                  Whether you're just starting to explore conception or have been trying for some time, we're here to listen, guide, and empower you.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mt-8">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="h-6 w-6 text-sage-600" />
                    </div>
                    <h3 className="font-semibold text-sage-800 mb-2">Real Answers</h3>
                    <p className="text-sm text-sage-600">Evidence-based approaches tailored to your unique needs</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Heart className="h-6 w-6 text-amber-600" />
                    </div>
                    <h3 className="font-semibold text-sage-800 mb-2">Holistic Support</h3>
                    <p className="text-sm text-sage-600">Comprehensive care for mind, body, and spirit</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-12 h-12 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Star className="h-6 w-6 text-sage-600" />
                    </div>
                    <h3 className="font-semibold text-sage-800 mb-2">Clear Path Forward</h3>
                    <p className="text-sm text-sage-600">Practical guidance and ongoing support</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Location & Booking Section */}
      <section className="py-16 px-4 md:px-6 bg-white border-b border-amber-100">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
              📍 Location & Booking
            </h2>
          </div>

          <Card className="border-2 border-sage-200 shadow-lg bg-white max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-sage-800 text-lg mb-2">Women and the Way</h3>
                  <p className="text-sage-600">Currimundi, Sunshine Coast QLD</p>
                </div>
                
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-amber-700 font-medium">🗓 Limited Spots Available</p>
                  <p className="text-sm text-amber-600 mt-1">This offer is available for a limited time</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 px-4 md:px-6 bg-sage-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
            ❤️ Ready to Feel Calm, Clear, and Confident on Your Fertility Journey?
          </h2>
          <p className="text-xl text-sage-600 mb-8 max-w-3xl mx-auto">
            Let us support you—with ancient wisdom, modern science, and the care you deserve. Book today and take the first step toward a well-nourished fertility path.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-medium"
            >
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Book Your Fertility Package Now
              </a>
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-4 text-lg font-medium"
            >
              <a href="tel:0415537784">Call Us at 0415 537 784</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FertilityPackagePage; 